// Refresh environment drawer. Three regions: non-scrolling header, one scrolling
// body, sticky footer. Nothing inside the body may introduce a second scroll box.
export const monoFont =
  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// Refresh-drawer tones the handoff's table does not cover. Light keeps today's
// hex; dark reuses the palette's own status and surface tokens.
const tone = (c) => ({
  brandPressed: c.isDark ? c.btnHover : "#0B3F94",
  brandSoftBg: c.isDark ? c.selected : "#EEF5FD",
  brandFaintBg: c.isDark ? c.tint : "#F8FBFF",
  canvasBg: c.isDark ? c.bg : "#F7F8FA",
  amberTint: c.isDark ? c.warnBg : "#FDF3E3",
  amberInk: c.isDark ? c.warnText : "#9A6100",
  amberEdge: c.isDark ? c.warnFg : "#F2D9A6",
  greenTint: c.isDark ? c.okBg : "#E9F7EC",
  redTint: c.isDark ? c.errBg : "#FBEAEA",
  redInk: c.isDark ? c.errText : "#C0322B",
  redHoverBg: c.isDark ? c.errBg : "#FDECEC",
  redHoverInk: c.isDark ? c.errFg : "#B3261E",
});

export const refreshDrawerStyles = (colors) => {
  const t = tone(colors);

  const ACTION = colors.btn;
  const ACTION_HOVER = colors.btnHover;
  const ACTION_PRESSED = t.brandPressed;
  const ROW_HOVER = colors.openRow;
  const SELECTED_TINT = t.brandSoftBg;
  const FOCUS_RING = "rgba(16,89,208,.12)";

  const INK = colors.ctlInkHover;
  const BODY = colors.ctlInk;
  const MUTED = colors.railMuted;
  const LABEL = colors.railHeading;

  const CARD_BORDER = colors.menuBorder;
  const CONTROL_BORDER = colors.ctl;
  const DIVIDER = colors.railRule;

  const CANVAS = t.canvasBg;
  const PANEL = colors.drawerBody;

  const AMBER_TINT = t.amberTint;
  const AMBER_INK = t.amberInk;
  const GREEN_TINT = t.greenTint;
  const GREEN_INK = colors.onDot;
  const RED_TINT = t.redTint;
  const RED_INK = t.redInk;
  const INFO_TINT = colors.actionTintBg;
  const INFO_BORDER = colors.ctlBorderHover;

  const DISABLED_BG = colors.disBg;
  const DISABLED_INK = colors.disFg;

  // Entry key | registry | image name | tag | remove. Fixed so both groups scan
  // against the same rulers and long keys ellipsis instead of reflowing the row.
  const ROW_GRID = "minmax(0,1fr) 132px minmax(0,1.15fr) 88px 26px";

  // A manifest group can hold hundreds of rows; ManifestGroup virtualizes them
  // with react-virtuoso rather than mounting every row at once. ROW_HEIGHT is
  // only an estimate for sizing the scroll window — Virtuoso measures each
  // row's real height itself, so a slightly-off estimate only affects how
  // much of a partial row peeks at the bottom, not correctness.
  const ROW_HEIGHT = 49;
  const MAX_VISIBLE_ROWS = 8;

  const pillBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "22px",
    px: "9px",
    borderRadius: "999px",
    fontSize: 11.5,
    fontWeight: 500,
    whiteSpace: "nowrap",
  };

  const controlBase = {
    height: "34px",
    borderRadius: "6px",
    fontSize: 13,
    backgroundColor: colors.surface,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: CONTROL_BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ACTION },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ACTION,
      borderWidth: "1px",
    },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${FOCUS_RING}` },
    "&.Mui-disabled": {
      backgroundColor: DISABLED_BG,
      color: DISABLED_INK,
      cursor: "not-allowed",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: CARD_BORDER },
    },
  };

  const buttonBase = {
    height: "36px",
    px: "14px",
    borderRadius: "6px",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  const refreshDrawer = {
    paper: (width = 720) => ({
      width: { xs: "100%", md: width },
      maxWidth: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderLeft: `1px solid ${CONTROL_BORDER}`,
      boxShadow: "-8px 0 32px rgba(16,24,40,.16)",
      backgroundColor: PANEL,
    }),
    backdrop: { backgroundColor: "rgba(16,24,40,.32)" },

    header: {
      flexShrink: 0,
      px: "20px",
      pt: "16px",
      backgroundColor: colors.surface,
      borderBottom: `1px solid ${DIVIDER}`,
    },
    headerTop: {
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
    },
    headerText: { flex: 1, minWidth: 0 },
    title: { fontSize: 18, fontWeight: 700, color: INK },
    metaRow: {
      mt: "4px",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "8px",
      fontSize: 12.5,
      color: MUTED,
    },
    metaMono: { fontFamily: monoFont, fontSize: 12.5, color: BODY },
    metaDot: { color: LABEL },
    metaOwnerLabel: { fontSize: 12, color: LABEL },
    closeButton: {
      flexShrink: 0,
      width: "30px",
      height: "30px",
      borderRadius: "6px",
      border: `1px solid ${CONTROL_BORDER}`,
      color: MUTED,
      "&:hover": {
        backgroundColor: ROW_HOVER,
        borderColor: ACTION,
        color: ACTION,
      },
    },

    // Underline tabs. The active tab owns the 2px border; inactive tabs keep a
    // transparent one so the row height never shifts on selection.
    tabRow: {
      mt: "12px",
      display: "flex",
      alignItems: "flex-end",
      gap: "18px",
    },
    tab: (active) => ({
      minWidth: 0,
      px: 0,
      pb: "9px",
      fontSize: 13.5,
      fontWeight: active ? 600 : 500,
      textTransform: "none",
      color: active ? ACTION : MUTED,
      borderBottom: `2px solid ${active ? ACTION : "transparent"}`,
      borderRadius: 0,
      "&:hover": {
        backgroundColor: "transparent",
        color: active ? ACTION : BODY,
      },
    }),
    tabHint: {
      ml: "auto",
      pb: "11px",
      fontSize: 12.5,
      color: LABEL,
      whiteSpace: "nowrap",
    },
    // Review and submitted hide the tabs but keep the header's bottom padding.
    tabRowSpacer: { height: "12px" },

    body: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      backgroundColor: PANEL,
      px: "20px",
      py: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },

    card: {
      borderRadius: "8px",
      border: `1px solid ${CARD_BORDER}`,
      backgroundColor: colors.surface,
    },
    cardHead: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      px: "14px",
      py: "10px",
      borderBottom: `1px solid ${DIVIDER}`,
    },
    // The group header splits into a clickable expander plus a trailing add
    // action, so the add button never nests inside the toggle button.
    cardHeadRow: { display: "flex", alignItems: "center", pr: "8px" },
    cardHeadClickable: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flex: 1,
      minWidth: 0,
      px: "14px",
      py: "10px",
      border: "none",
      borderRadius: "8px 0 0 8px",
      backgroundColor: "transparent",
      font: "inherit",
      cursor: "pointer",
      textAlign: "left",
      "&:hover": { backgroundColor: ROW_HOVER },
    },
    addRowButton: {
      flexShrink: 0,
      width: "26px",
      height: "26px",
      borderRadius: "5px",
      border: `1px solid ${CONTROL_BORDER}`,
      color: MUTED,
      "&:hover": {
        backgroundColor: ROW_HOVER,
        borderColor: ACTION,
        color: ACTION,
      },
    },
    cardTitle: { fontSize: 14, fontWeight: 600, color: INK },
    // Title over a description line, for heads that need more than a label.
    cardHeadText: {
      display: "flex",
      flexDirection: "column",
      gap: "3px",
      minWidth: 0,
    },
    cardHeadDescription: { fontSize: 12.5, color: MUTED },
    cardSubtitle: {
      fontSize: 12.5,
      color: MUTED,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cardSubtitleMono: { fontFamily: monoFont, color: BODY },
    cardCount: {
      ml: "auto",
      fontSize: 12.5,
      color: MUTED,
      whiteSpace: "nowrap",
    },
    eyebrow: {
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: LABEL,
    },
    headBadges: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      minWidth: 0,
    },
    chevron: { color: LABEL, display: "flex", alignItems: "center", px: "4px" },
    cardBody: {
      px: "14px",
      py: "12px",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    },

    // Label column left, control right. Shared by every single-field row so the
    // controls line up across cards.
    fieldRow: {
      display: "grid",
      gridTemplateColumns: "120px minmax(0, 1fr)",
      alignItems: "center",
      gap: "12px",
    },
    fieldLabel: { fontSize: 13, fontWeight: 500, color: BODY },
    fieldLabelMono: { fontFamily: monoFont, fontSize: 12.5, color: BODY },
    fieldHelper: { gridColumn: "2", mt: "-4px", fontSize: 12.5, color: MUTED },
    // Label | segmented pill | resolved-branch-or-input, with the helper spanning
    // everything but the label column. Sits under the release row in the Target
    // card, so it repeats that card's 120px label column.
    branchRow: {
      display: "grid",
      gridTemplateColumns: "120px auto minmax(0, 1fr)",
      alignItems: "center",
      columnGap: "12px",
      rowGap: "6px",
      pt: "12px",
      borderTop: `1px solid ${DIVIDER}`,
    },
    branchHelper: { gridColumn: "2 / -1", fontSize: 12.5, color: MUTED },
    segmentedPill: {
      display: "inline-flex",
      border: `1px solid ${CONTROL_BORDER}`,
      borderRadius: "6px",
      overflow: "hidden",
      height: "34px",
    },
    segment: (active) => ({
      display: "flex",
      alignItems: "center",
      height: "32px",
      px: "14px",
      border: "none",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: active ? 500 : 400,
      lineHeight: 1,
      backgroundColor: active ? SELECTED_TINT : colors.surface,
      color: active ? ACTION_HOVER : MUTED,
      transition: "background-color 0.15s, color 0.15s",
      "&:not(:last-of-type)": { borderRight: `1px solid ${CONTROL_BORDER}` },
      "&:hover": { backgroundColor: active ? SELECTED_TINT : ROW_HOVER },
    }),
    branchReadOnly: {
      fontFamily: monoFont,
      fontSize: 13,
      color: LABEL,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cardDescription: { fontSize: 12.5, color: MUTED, mt: "-2px" },
    select: { ...controlBase },
    input: {
      "& .MuiOutlinedInput-root": { ...controlBase },
      "& .MuiOutlinedInput-input": { fontSize: 13 },
    },
    monoInput: {
      "& .MuiOutlinedInput-root": { ...controlBase, fontFamily: monoFont },
      "& .MuiOutlinedInput-input": { fontFamily: monoFont, fontSize: 13 },
    },
    monoInputCompact: {
      "& .MuiOutlinedInput-root": {
        ...controlBase,
        height: "32px",
        fontFamily: monoFont,
      },
      "& .MuiOutlinedInput-input": {
        fontFamily: monoFont,
        fontSize: 13,
        py: 0,
      },
    },

    // Standard mode lists the flags one per row; the checkbox column is fixed so
    // labels and descriptions share a left edge.
    checklist: { display: "flex", flexDirection: "column" },
    checkRow: {
      display: "grid",
      gridTemplateColumns: "17px minmax(0, 1fr)",
      columnGap: "10px",
      alignItems: "start",
      py: "8px",
      borderBottom: `1px solid ${DIVIDER}`,
      cursor: "pointer",
      "&:last-of-type": { borderBottom: "none" },
    },
    checkbox: {
      p: 0,
      mt: "1px",
      width: "17px",
      height: "17px",
      color: CONTROL_BORDER,
      "&.Mui-checked": { color: ACTION },
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    checkLabel: {
      fontSize: 13.5,
      fontWeight: 500,
      color: INK,
      lineHeight: "18px",
    },
    checkDescription: { fontSize: 12.5, color: MUTED, lineHeight: "17px" },

    // Read-only pills for the "unchanged" card and neutral status.
    pillRow: { display: "flex", flexWrap: "wrap", gap: "8px" },
    neutralPill: {
      ...pillBase,
      backgroundColor: colors.divider,
      color: BODY,
      border: `1px solid ${CARD_BORDER}`,
    },
    countBadge: {
      ...pillBase,
      height: "20px",
      px: "7px",
      fontSize: 11,
      fontFamily: monoFont,
      backgroundColor: CANVAS,
      color: MUTED,
      border: `1px solid ${CARD_BORDER}`,
    },

    // Custom mode carries the component flags as checkbox chips.
    chipRow: { display: "flex", flexWrap: "wrap", gap: "8px" },
    checkChip: (checked) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      height: "28px",
      pl: "8px",
      pr: "10px",
      borderRadius: "6px",
      fontSize: 12.5,
      fontWeight: 500,
      whiteSpace: "nowrap",
      cursor: "pointer",
      userSelect: "none",
      color: checked ? ACTION_HOVER : BODY,
      backgroundColor: checked ? t.brandSoftBg : colors.surface,
      border: `1px solid ${checked ? INFO_BORDER : CONTROL_BORDER}`,
      "&:hover": { borderColor: ACTION },
    }),
    checkChipBox: {
      p: 0,
      width: "15px",
      height: "15px",
      color: CONTROL_BORDER,
      "&.Mui-checked": { color: ACTION },
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },

    // Filter field: removable chips share the box with the free-text input;
    // suggestions render as "+ key" buttons beneath it.
    filterField: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "6px",
      minHeight: "34px",
      px: "8px",
      py: "3px",
      borderRadius: "6px",
      border: `1px solid ${CONTROL_BORDER}`,
      backgroundColor: colors.surface,
      cursor: "text",
      "&:hover": { borderColor: ACTION },
      "&:focus-within": {
        borderColor: ACTION,
        boxShadow: `0 0 0 3px ${FOCUS_RING}`,
      },
    },
    filterChip: {
      ...pillBase,
      height: "24px",
      pl: "9px",
      pr: "4px",
      gap: "2px",
      fontFamily: monoFont,
      fontSize: 11.5,
      backgroundColor: t.brandSoftBg,
      color: ACTION_HOVER,
      border: `1px solid ${INFO_BORDER}`,
    },
    filterChipRemove: {
      p: 0,
      width: "16px",
      height: "16px",
      color: ACTION_HOVER,
      "&:hover": { backgroundColor: "transparent", color: ACTION_PRESSED },
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    filterInput: {
      flex: 1,
      minWidth: "140px",
      fontFamily: monoFont,
      fontSize: 13,
      color: INK,
      "& input": {
        p: "4px 2px",
        height: "18px",
        "&::placeholder": { color: LABEL, opacity: 1 },
      },
    },
    suggestionRow: { display: "flex", flexWrap: "wrap", gap: "6px" },
    suggestionButton: {
      ...pillBase,
      height: "24px",
      px: "9px",
      fontFamily: monoFont,
      fontSize: 11.5,
      textTransform: "none",
      minWidth: 0,
      color: BODY,
      backgroundColor: colors.surface,
      border: `1px solid ${CONTROL_BORDER}`,
      "&:hover": {
        backgroundColor: ROW_HOVER,
        borderColor: ACTION,
        color: ACTION,
      },
    },
    scopeChip: (checked) => ({
      height: "30px",
      px: "12px",
      borderRadius: "6px",
      fontSize: 12.5,
      fontWeight: 500,
      textTransform: "none",
      whiteSpace: "nowrap",
      color: checked ? ACTION_HOVER : MUTED,
      backgroundColor: checked ? t.brandSoftBg : colors.surface,
      border: `1px solid ${checked ? colors.ctlBorderHover : CONTROL_BORDER}`,
      "&:hover": {
        backgroundColor: checked ? t.brandSoftBg : ROW_HOVER,
        borderColor: ACTION,
      },
    }),

    // Manifest rows
    rowHeader: {
      display: "grid",
      gridTemplateColumns: ROW_GRID,
      alignItems: "center",
      gap: "10px",
      px: "14px",
      py: "6px",
      backgroundColor: CANVAS,
      borderBottom: `1px solid ${DIVIDER}`,
    },
    rowHeaderCell: {
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: LABEL,
    },
    row: (overridden) => ({
      display: "grid",
      gridTemplateColumns: ROW_GRID,
      alignItems: "center",
      gap: "10px",
      px: "14px",
      py: "9px",
      borderBottom: `1px solid ${DIVIDER}`,
      backgroundColor: overridden ? t.brandFaintBg : "transparent",
      "&:last-of-type": { borderBottom: "none" },
      "&:hover": { backgroundColor: ROW_HOVER },
    }),
    rowKeyCell: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      minWidth: 0,
    },
    rowKeyText: {
      minWidth: 0,
      display: "flex",
      alignItems: "baseline",
      gap: "6px",
    },
    rowKey: {
      fontFamily: monoFont,
      fontSize: 13,
      color: INK,
      lineHeight: "17px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    rowCommit: {
      flexShrink: 0,
      fontFamily: monoFont,
      fontSize: 11,
      color: DISABLED_INK,
      whiteSpace: "nowrap",
    },
    rowTagInput: {
      "& .MuiOutlinedInput-root": {
        ...controlBase,
        height: "30px",
        fontFamily: monoFont,
      },
      "& .MuiOutlinedInput-input": {
        fontFamily: monoFont,
        fontSize: 12,
        py: 0,
        textAlign: "center",
      },
    },
    // 6px dot marks a row whose values no longer match the manifest.
    overrideDot: {
      flexShrink: 0,
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      backgroundColor: ACTION,
    },
    rowSelect: {
      ...controlBase,
      height: "30px",
      fontFamily: monoFont,
      fontSize: 12,
    },
    rowInput: {
      "& .MuiOutlinedInput-root": {
        ...controlBase,
        height: "30px",
        fontFamily: monoFont,
      },
      "& .MuiOutlinedInput-input": {
        fontFamily: monoFont,
        fontSize: 12,
        py: 0,
      },
    },
    // Manifest rows have no remove control, so they reserve the trailing column.
    rowActionSpacer: { width: "26px" },
    removeRowButton: {
      width: "26px",
      height: "26px",
      borderRadius: "5px",
      color: LABEL,
      "&:hover": { backgroundColor: t.redHoverBg, color: t.redHoverInk },
    },
    emptyRow: {
      px: "14px",
      py: "16px",
      fontSize: 12.5,
      textAlign: "center",
      color: LABEL,
    },
    loadingBox: {
      display: "flex",
      justifyContent: "center",
      py: "40px",
      color: LABEL,
    },

    // Review
    reviewRow: {
      display: "grid",
      gridTemplateColumns: "130px minmax(0, 1fr)",
      gap: "12px",
      py: "7px",
      borderBottom: `1px solid ${DIVIDER}`,
      "&:last-of-type": { borderBottom: "none" },
    },
    reviewLabel: { fontSize: 12.5, color: LABEL },
    reviewValue: {
      fontSize: 13,
      color: BODY,
      minWidth: 0,
      wordBreak: "break-word",
    },
    reviewMono: {
      fontFamily: monoFont,
      fontSize: 12.5,
      color: INK,
      wordBreak: "break-all",
    },
    // key · registry/name · tag, one line per override.
    overrideLine: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.3fr) auto",
      gap: "12px",
      alignItems: "center",
      py: "7px",
      borderBottom: `1px solid ${DIVIDER}`,
      "&:last-of-type": { borderBottom: "none" },
    },
    overrideKey: {
      fontFamily: monoFont,
      fontSize: 12.5,
      color: INK,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    overrideName: {
      fontFamily: monoFont,
      fontSize: 12.5,
      color: BODY,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    overrideTag: {
      fontFamily: monoFont,
      fontSize: 12.5,
      color: ACTION_HOVER,
      whiteSpace: "nowrap",
    },
    reviewWarn: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      px: "14px",
      py: "10px",
      borderRadius: "8px",
      border: `1px solid ${t.amberEdge}`,
      backgroundColor: AMBER_TINT,
      color: AMBER_INK,
    },
    reviewWarnText: { fontSize: 12.5, color: AMBER_INK, lineHeight: "17px" },
    // Same amber callout, but top-aligned because the copy always wraps.
    calloutWarn: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      px: "14px",
      py: "12px",
      borderRadius: "8px",
      border: `1px solid ${t.amberEdge}`,
      backgroundColor: AMBER_TINT,
      color: AMBER_INK,
    },
    calloutWarnIcon: { flexShrink: 0, fontSize: 18, mt: "-1px" },
    calloutWarnMono: { fontFamily: monoFont, fontSize: 12.5 },

    // Submitted step
    infoBanner: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      px: "14px",
      py: "12px",
      borderRadius: "8px",
      border: `1px solid ${INFO_BORDER}`,
      backgroundColor: INFO_TINT,
      color: ACTION_HOVER,
    },
    infoBannerTitle: {
      fontSize: 13.5,
      fontWeight: 600,
      color: ACTION_HOVER,
      lineHeight: "18px",
    },
    infoBannerDetail: { fontSize: 12.5, color: BODY, lineHeight: "17px" },
    stepRow: {
      display: "grid",
      gridTemplateColumns: "20px minmax(0, 1fr) auto",
      alignItems: "center",
      columnGap: "10px",
      py: "9px",
      borderBottom: `1px solid ${DIVIDER}`,
      "&:last-of-type": { borderBottom: "none" },
    },
    stepGlyph: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "20px",
      height: "20px",
    },
    stepDot: (state) => ({
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor:
        state === "done"
          ? GREEN_INK
          : state === "failed"
          ? RED_INK
          : CONTROL_BORDER,
    }),
    stepLabel: (state) => ({
      fontSize: 13.5,
      color: state === "pending" ? LABEL : INK,
    }),
    stepStatus: (state) => ({
      fontSize: 12,
      fontFamily: monoFont,
      color:
        state === "done"
          ? GREEN_INK
          : state === "failed"
          ? RED_INK
          : state === "active"
          ? AMBER_INK
          : LABEL,
      whiteSpace: "nowrap",
    }),
    statusPill: (tone) => ({
      ...pillBase,
      backgroundColor:
        tone === "green" ? GREEN_TINT : tone === "red" ? RED_TINT : AMBER_TINT,
      color:
        tone === "green" ? GREEN_INK : tone === "red" ? RED_INK : AMBER_INK,
    }),

    footer: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      px: "20px",
      py: "12px",
      borderTop: `1px solid ${DIVIDER}`,
      backgroundColor: colors.surface,
    },
    footerSummary: { minWidth: 0, fontSize: 12.5, color: MUTED },
    footerActions: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexShrink: 0,
    },
    secondaryButton: {
      ...buttonBase,
      color: MUTED,
      borderColor: CONTROL_BORDER,
      "&:hover": {
        borderColor: ACTION,
        color: ACTION,
        backgroundColor: ROW_HOVER,
      },
    },
    primaryButton: {
      ...buttonBase,
      backgroundColor: ACTION,
      boxShadow: "none",
      "&:hover": { backgroundColor: ACTION_HOVER, boxShadow: "none" },
      "&:active": { backgroundColor: ACTION_PRESSED },
      "&.Mui-disabled": { backgroundColor: DISABLED_BG, color: DISABLED_INK },
    },
  };

  return { refreshDrawer, ROW_GRID, ROW_HEIGHT, MAX_VISIBLE_ROWS, ACTION };
};
